import {
    CreateZendeskTicketV2,
    createZendeskTicketSchemaV2,
} from '@apis/requests/zendesk-v2/create-ticket-v2';
import TextArea from '@components/atoms/inputs/text-area';
import { color } from '@helpers/constants';
import { getGenericErrorNotification } from '@helpers/i18n';
import { isFieldValid } from '@helpers/yup';
import { yupResolver } from '@helpers/yupResolver';
import useZendesk from '@hooks/requests/useZendesk-v2/useZendesk-v2';
import useUser from '@hooks/useUser';
import {
    AutocompleteSelect,
    Button,
    Icon,
    Popover,
    RadioButton,
    Separator,
    TextInput,
    c,
    useNotifications,
} from '@minds/minds-react-sdk';
import { authAtom } from '@stateAtoms/authAtom';
import zendeskPopoverAtom from '@stateAtoms/zendeskAtom';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FileInput from '../file-input/file-input';
import style from './zendesk-popover-v2.module.scss';

const possibleErrors = [
    'zendesk_invalid_file_type',
    'zendesk_attachment_too_big',
    'subject_required',
    'description_required',
    'email_required',
    'first_name_required',
] as const;

interface Props {
    onClose?: () => unknown;
    onSend?: () => unknown;
}

export default function ZendeskPopover(props: Props) {
    const [zendeskAtom, setZendeskAtom] = useAtom(zendeskPopoverAtom);
    const [isMinimized, setIsMinimized] = useState(false);
    const { t } = useTranslation('public');
    const wrapperRef = useRef(null);
    const auth = useAtomValue(authAtom);
    const user = useUser();
    const api = useZendesk();
    const { addPush } = useNotifications();

    const [pageSelection, setPageSelection] = useState<
        'thisPage' | 'otherPage' | ''
    >('thisPage');

    const [persistenceSelection, setPersistenceSelection] = useState<
        'yes' | 'no' | ''
    >('yes');

    const [showOtherPageUrl, setShowOtherPageUrl] = useState<boolean>(false);

    const [subject, setSubject] = useState<string>('');

    useEffect(() => {
        setLabel(t('selectSubject'));
    }, [t]);

    const [label, setLabel] = useState<string>('');

    const subjectOptions = [
        {
            label: t('error'),
            value: 'Erro',
        },
        {
            label: t('functionalityQuestion'),
            value: 'Dúvida de Funcionalidade',
        },
        {
            label: t('others'),
            value: 'Outros',
        },
    ];

    const {
        register,
        setValue,
        reset,
        handleSubmit,
        formState: { errors, dirtyFields, isSubmitted, isSubmitting },
        control,
    } = useForm<CreateZendeskTicketV2>({
        resolver: yupResolver(createZendeskTicketSchemaV2),
        mode: 'onChange',
        defaultValues: {
            pageSelection: 'thisPage',
            persistenceSelection: 'sim',
            pageURL: window.location.href,
        },
    });

    useEffect(() => {
        if (auth.isLoggedIn) {
            setValue('name', user?.name);
            setValue('email', user?.email);
        }
    }, [user, setValue]);

    const closeZendeskPopover = () => {
        setZendeskAtom((z) => ({ ...z, visible: false }));
    };

    const handleExpandPopover = () => {
        setIsMinimized((m) => !m);
    };

    useEffect(() => {
        if (errors.pageSelection) {
            addPush({
                title: t('oops_something_went_wrong'),
                content: t('select_a_page'),
                variant: 'error',
            });
        }
    }, [errors.pageSelection]);

    useEffect(() => {
        if (errors.persistenceSelection) {
            addPush({
                title: t('oops_something_went_wrong'),
                content: t('select_persistence_selection'),
                variant: 'error',
            });
        }
    }, [errors.persistenceSelection]);

    async function handleFormSubmission(data: CreateZendeskTicketV2) {
        try {
            if (
                data.pageSelection === 'thisPage' &&
                data.pageURL !== window.location.href
            ) {
                // eslint-disable-next-line no-param-reassign
                data.pageURL = window.location.href;
            }

            const response = await api.createTicketV2(data);

            if (!response.success) {
                if (response.errors) {
                    const error = Object.values(
                        response.errors,
                    )[0][0] as (typeof possibleErrors)[number];

                    addPush({
                        title: t('oops_something_went_wrong'),
                        content: possibleErrors.includes(error)
                            ? t(error)
                            : t('please_try_again'),
                        variant: 'error',
                    });
                    return;
                }

                addPush(getGenericErrorNotification());
                return;
            }

            addPush({
                title: t('ticketSuccess'),
                content: t('successMessage'),
                variant: 'success',
                timeout: 600 * 1000,
            });

            reset();
            closeZendeskPopover();

            if (props.onSend) {
                props.onSend();
                return;
            }

            props.onClose?.();
        } catch (error) {
            addPush({
                title: t('url_required'),
                content: 'Erro ao enviar ticket. Tente novamente.',
                variant: 'error',
            });
        }
    }

    function handleOtherPageSelection() {
        setPageSelection('otherPage');
        setShowOtherPageUrl(true);
        setValue('pageSelection', 'otherPage', {
            shouldValidate: true,
            shouldDirty: true,
        });
        setValue('pageURL', '');
    }

    function handleThisPageSelection() {
        setPageSelection('thisPage');
        setValue('pageSelection', 'thisPage', {
            shouldValidate: true,
            shouldDirty: true,
        });
        setValue('pageURL', window.location.href);
        setShowOtherPageUrl(false);
    }
    return (
        <div>
            <Popover
                targetRef={wrapperRef}
                visible={zendeskAtom.visible}
                wrapperClassName={style.zendeskContainer}
                customCardClass={c(
                    style.zendeskCard,
                    isMinimized && style.minimizedCard,
                )}
            >
                <div className={style.header}>
                    <div className={style.headerContainer}>
                        <h5 className={style.headerText}>{t('readyToHelp')}</h5>
                    </div>
                    <div
                        className={c(
                            style.headerContainer,
                            isMinimized && style.headerContainerMinimized,
                        )}
                        onClick={handleExpandPopover}
                        aria-hidden="true"
                    >
                        <h5
                            className={c(
                                style.headerText,
                                isMinimized && style.clickableHeader,
                            )}
                        >
                            {t('readyToHelp')}
                        </h5>
                        <div className={style.minimizeIcon}>
                            <Icon
                                name={isMinimized ? 'i-ph-plus' : 'i-ph-minus'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleExpandPopover();
                                }}
                                color={color('gray-300')}
                            />
                        </div>
                    </div>
                </div>
                <div className={style.body}>
                    <div className={style.content}>
                        <form
                            className={style.zendeskForm}
                            onSubmit={handleSubmit(handleFormSubmission)}
                        >
                            <div data-testid="subject">
                                <Controller
                                    control={control}
                                    name="subject"
                                    render={({ field }) => (
                                        <AutocompleteSelect
                                            {...field}
                                            id="subject"
                                            data-testid="subject"
                                            options={subjectOptions}
                                            placeholder={t('selectSubject')}
                                            value={subjectOptions.find(
                                                (x) => x.value === field.value,
                                            )}
                                            onChange={(value) => {
                                                field.onChange(value?.value);
                                                if (value) {
                                                    setSubject(value.value);
                                                    setLabel(value.label);
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <label
                                className={style.title}
                                htmlFor="description"
                            >
                                {t('description')}
                            </label>
                            <TextArea
                                {...register('description')}
                                className={style.description}
                                name="description"
                                placeholder={t('enterDescription')}
                                noResize
                                valid={isFieldValid(
                                    errors.description,
                                    dirtyFields.description || isSubmitted,
                                )}
                                id="description"
                            />
                            <label className={style.title} htmlFor="page">
                                {t('page')}
                            </label>
                            <div className={style.radioGroup}>
                                <RadioButton
                                    className={style.radioItem}
                                    label={t('thisPage')}
                                    id="thisPage"
                                    name="pageSelection"
                                    checked={pageSelection === 'thisPage'}
                                    onChange={handleThisPageSelection}
                                />
                                <div data-testid="otherPage">
                                    <RadioButton
                                        className={style.radioItem}
                                        label={t('otherPage')}
                                        id="otherPage"
                                        name="pageSelection"
                                        checked={pageSelection === 'otherPage'}
                                        onChange={handleOtherPageSelection}
                                    />
                                </div>
                            </div>
                            <input
                                type="hidden"
                                {...register('pageSelection')}
                            />
                            {showOtherPageUrl && (
                                <div
                                    data-testid="otherPageUrl"
                                    className={style.urlFieldContainer}
                                >
                                    <TextInput
                                        {...register('pageURL')}
                                        className={style.zendeskInput}
                                        label={t('Url')}
                                        name="pageURL"
                                        placeholder="https://exemplo.com/pagina-do-erro"
                                        id="pageURL"
                                        valid={isFieldValid(
                                            errors.pageURL,
                                            dirtyFields.pageURL || isSubmitted,
                                        )}
                                    />
                                </div>
                            )}
                            <label
                                className={style.title}
                                htmlFor="persistence"
                            >
                                {t('errorPersistence')}
                            </label>
                            <div className={style.radioGroup}>
                                <RadioButton
                                    className={style.radioItem}
                                    label={t('yes')}
                                    id="errorPersistence"
                                    name="persistenceSelection"
                                    checked={persistenceSelection === 'yes'}
                                    onChange={() => {
                                        setPersistenceSelection('yes');
                                        setValue(
                                            'persistenceSelection',
                                            'sim',
                                            {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            },
                                        );
                                    }}
                                />
                                <RadioButton
                                    className={style.radioItem}
                                    label={t('no')}
                                    id="noErrorPersistence"
                                    name="persistenceSelection"
                                    checked={persistenceSelection === 'no'}
                                    onChange={() => {
                                        setPersistenceSelection('no');
                                        setValue(
                                            'persistenceSelection',
                                            'não',
                                            {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            },
                                        );
                                    }}
                                />
                            </div>
                            <input
                                type="hidden"
                                {...register('persistenceSelection')}
                            />
                            <label className={style.title} htmlFor="file">
                                {t('attachment')}
                            </label>
                            <FileInput
                                control={control}
                                name="file"
                                id="file"
                                acceptedTypes={[
                                    '.jpg',
                                    '.jpeg',
                                    '.png',
                                    '.pdf',
                                ]}
                                invalid={!!errors.file}
                                removeText={t('remove')}
                            />
                            <Separator />
                            <span className="text-xs text-gray-500">
                                <Icon name="i-ph-info" className="mr-1" />
                                {t('sendReportTip')}
                            </span>
                            <Separator />
                            <div className={style.buttonsContainer}>
                                <Button
                                    className={c(style.actionButton)}
                                    content={t('cancel')}
                                    onClick={closeZendeskPopover}
                                    variant="outline-dark"
                                    id="cancel"
                                />
                                <Button
                                    className={c(style.actionButton)}
                                    content={t('sendReport')}
                                    type="submit"
                                    loading={isSubmitting}
                                    id="send"
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </Popover>
        </div>
    );
}
